import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { 
  Plus, 
  Edit2, 
  Trash2, 
  RefreshCw, 
  Search,
  Filter,
  Star,
  TrendingUp,
  User,
  Building,
  Phone,
  Mail,
  Calendar,
  DollarSign,
  Target,
  Eye,
  ExternalLink,
  Trophy,
  Clock,
  Award,
  Activity,
  X,
  ChevronRight,
  List,
  Grid,
  Download,
  MoreVertical,
  ChevronDown,
  Tag
} from 'lucide-react';
import OpportunityForm from './OpportunityForm';
import OpportunityDetail from './OpportunityDetail';
import DynamicEntityTable from '../../components/EntityManagement/DynamicEntityTable';
import SpreadsheetView from '../../components/EntityManagement/SpreadsheetView';
import EntityFilter from '../../components/EntityManagement/EntityFilter';
import BulkTagManager from '../../components/TagManagement/BulkTagManager';
import { useTenantProtection } from '../../hooks/useTenantProtection';
import { useAuth } from '../../contexts/AuthContext';
import { formatCurrency } from '../../utils/currency';
import { useEntityConfig } from '../../hooks/useEntityConfig';
import { getFieldOptionsSync, isSelectField as checkIsSelectField } from '../../hooks/useFieldOptions';
import { useTranslationReady } from '../../hooks/useTranslationReady';
import DependencyDeleteDialog from '../../components/UI/DependencyDeleteDialog';
import { toast } from 'react-hot-toast';

const Opportunities = () => {
  const { t, i18n, ready, translationsReady, safeT } = useTranslationReady([
    'opportunities.opportunityName',
    'opportunities.addOpportunity',
    'opportunities.searchPlaceholder',
    'opportunities.stage',
    'opportunities.value'
  ]);
  const location = useLocation();
  const { getProtectedTenantCode } = useTenantProtection();
  const { hasRole } = useAuth();
  
  // Load entity configuration
  const { config: entityConfig, loading: configLoading } = useEntityConfig('opportunity');
  
  const [opportunities, setOpportunities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [showDetail, setShowDetail] = useState(false);
  const [formMode, setFormMode] = useState('create');
  const [selectedOpportunity, setSelectedOpportunity] = useState(null);
  const [stats, setStats] = useState({});
  const [statsLoading, setStatsLoading] = useState(false);
  const [focusedOpportunityId, setFocusedOpportunityId] = useState(null);
  const [hasScrolledToOpportunity, setHasScrolledToOpportunity] = useState(false);
  const opportunityRefs = useRef({});
  const abortControllerRef = useRef(null);
  const deferredSelectionTimeoutRef = useRef(null);
  const deferredStatsTimeoutRef = useRef(null);
  const deferredSalespersonsTimeoutRef = useRef(null);

  // Filters and search
  const [filters, setFilters] = useState({
    search: '',
    stage: '',
    priority: '',
    category: '',
    industry: '',
    minValue: '',
    maxValue: '',
    probability: ''
  });
  
  // Advanced filters for EntityFilter component
  const [advancedFilters, setAdvancedFilters] = useState([]);
  const [selectedTag, setSelectedTag] = useState('');
  const [availableTags, setAvailableTags] = useState([]);
  const [tagsLoading, setTagsLoading] = useState(false);
  const activeAdvancedFilters = useMemo(() => (
    advancedFilters.filter(f =>
      f.field &&
      f.operator &&
      (f.operator === 'isNull' ||
        f.operator === 'isNotNull' ||
        (f.value !== undefined && f.value !== ''))
    )
  ), [advancedFilters]);
  const activeFiltersCount = useMemo(() => {
    const simpleCount = Object.values(filters).filter(value => value !== '').length;
    const tagCount = selectedTag ? 1 : 0;
    return simpleCount + tagCount + activeAdvancedFilters.length;
  }, [filters, selectedTag, activeAdvancedFilters]);
  const hasSimpleFilters = useMemo(
    () => Object.values(filters).some(value => value !== '') || Boolean(selectedTag),
    [filters, selectedTag]
  );
  const hasActiveFilters = activeFiltersCount > 0;
  
  // Dynamic list values for filters
  const [priorityOptions, setPriorityOptions] = useState([]);
  const [stageOptions, setStageOptions] = useState([]);
  const [categoryOptions, setCategoryOptions] = useState([]);
  
  // View mode and sorting
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'spreadsheet'
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  
  // UI state
  const [showFilters, setShowFilters] = useState(false);
  const [menuExpanded, setMenuExpanded] = useState(false);
  
  // Spreadsheet selection
  const [selectedOpportunities, setSelectedOpportunities] = useState(new Set());
  const [selectAll, setSelectAll] = useState(false);
  
  // Bulk actions
  const [showBulkTagManager, setShowBulkTagManager] = useState(false);
  
  // Salesperson filter state (for Admin/Manager)
  const [salespersonFilter, setSalespersonFilter] = useState('all');
  const [salespersons, setSalespersons] = useState([]);
  const [showSalespersonFilter, setShowSalespersonFilter] = useState(false);
  
  // Pagination
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0
  });

  // Load selection values from tenant config with global fallback
  const loadSelectionValues = async () => {
    const signal = abortControllerRef.current?.signal;
    try {
      console.log('🔍 Opportunities: Loading selection values from tenant config...');
      const response = await fetch('/api/tenant-selection/opportunity', {
        signal,
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        const selectionValues = data.selectionValues;
        
        console.log('🔍 Opportunities: Received selection values:', selectionValues);
        
        // Update Priority options
        if (selectionValues?.Priority?.values) {
          const priorityOpts = selectionValues.Priority.values.map(v => ({
            value: v.value,
            label: v.label,
            color: v.color
          }));
          setPriorityOptions(priorityOpts);
          console.log('✅ Opportunities: Set Priority options:', priorityOpts);
        }
        
        // Update Stage options
        if (selectionValues?.Stage?.values) {
          const stageOpts = selectionValues.Stage.values.map(v => ({
            value: v.value,
            label: v.label,
            color: v.color
          }));
          setStageOptions(stageOpts);
          console.log('✅ Opportunities: Set Stage options:', stageOpts);
        }
        
        // Update Category options
        if (selectionValues?.Category?.values) {
          const categoryOpts = selectionValues.Category.values.map(v => ({
            value: v.value,
            label: v.label
          }));
          setCategoryOptions(categoryOpts);
          console.log('✅ Opportunities: Set Category options:', categoryOpts);
        }
      } else {
        console.error('❌ Opportunities: Failed to fetch selection values, status:', response.status);
        // Fallback to default values
        setDefaultOptions();
      }
    } catch (error) {
      if (error?.name === 'AbortError') return;
      console.error('❌ Opportunities: Error loading selection values:', error);
      setDefaultOptions();
    }
  };

  const setDefaultOptions = () => {
    setPriorityOptions([
      { value: 'Low', label: 'Low' },
      { value: 'Medium', label: 'Medium' },
      { value: 'High', label: 'High' },
      { value: 'Critical', label: 'Critical' },
    ]);
    setStageOptions([
      { value: 'Qualified', label: 'Qualified' },
      { value: 'Proposal', label: 'Proposal' },
      { value: 'Negotiation', label: 'Negotiation' },
      { value: 'Closed Won', label: 'Closed Won' },
      { value: 'Closed Lost', label: 'Closed Lost' },
    ]);
    setCategoryOptions([
      { value: 'New Business', label: 'New Business' },
      { value: 'Existing Business', label: 'Existing Business' },
      { value: 'Renewal', label: 'Renewal' },
      { value: 'Upsell', label: 'Upsell' },
    ]);
  };

  const fetchAvailableTags = async () => {
    const signal = abortControllerRef.current?.signal;
    try {
      setTagsLoading(true);
      const response = await fetch('/api/tags?entityType=Opportunity', {
        signal,
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setAvailableTags(data.tags || []);
      }
    } catch (error) {
      if (error?.name === 'AbortError') return;
      console.error('Failed to fetch tags:', error);
    } finally {
      setTagsLoading(false);
    }
  };

  const handleTagDropdownFocus = () => {
    if (availableTags.length === 0 && !tagsLoading) {
      fetchAvailableTags();
    }
  };

  const fetchOpportunities = async (resetPage = false) => {
    const signal = abortControllerRef.current?.signal;
    try {
      setLoading(true);
      const page = resetPage ? 1 : pagination.page;
      const queryParams = new URLSearchParams({
        page: page.toString(),
        limit: pagination.limit.toString(),
        ...Object.fromEntries(
          Object.entries(filters).filter(([_, value]) => value !== '')
        )
      });
      
      // Add advanced filters if any
      const validFilters = advancedFilters.filter(f => 
        f.field && f.operator && (f.operator === 'isNull' || f.operator === 'isNotNull' || (f.value !== undefined && f.value !== ''))
      );
      if (validFilters.length > 0) {
        queryParams.set('filters', JSON.stringify(validFilters));
      } else {
        // Remove filters parameter if no valid filters
        queryParams.delete('filters');
      }
      
      if (selectedTag) {
        queryParams.set('tag', selectedTag);
      }

      // Add salesperson filter if set (Admin/Manager only)
      if (salespersonFilter && salespersonFilter !== 'all') {
        queryParams.set('salespersonId', salespersonFilter);
      }
      
      const tenantCode = getProtectedTenantCode();
      const selectedRole = localStorage.getItem('selectedRole');
      const res = await fetch(`/api/opportunities?${queryParams}`, {
        signal,
        headers: { 
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          ...(tenantCode ? { 'X-Tenant-Code': tenantCode } : {}),
          ...(selectedRole ? { 'X-Selected-Role': selectedRole } : {})
        }
      });
      
      if (!res.ok) throw new Error('Failed to fetch opportunities');
      const data = await res.json();
      if (signal?.aborted) return;
      const list = data.opportunities || [];
      setOpportunities(list);
      setPagination(data.pagination || pagination);
      if (data?.stats) {
        setStats(prev => ({
          ...prev,
          totalOpportunities: data.stats.totalOpportunities || 0,
          totalValue: data.stats.totalValue || 0
        }));
      }
      setError(null);
    } catch (err) {
      if (err?.name === 'AbortError') return;
      setError(err.message);
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  };

  const fetchStats = async () => {
    const signal = abortControllerRef.current?.signal;
    try {
      setStatsLoading(true);
      const tenantCode = getProtectedTenantCode();
      const res = await fetch('/api/analytics/win-loss-analysis?period=12months', {
        signal,
        headers: { 
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          ...(tenantCode ? { 'X-Tenant-Code': tenantCode } : {})
        }
      });
      if (res.ok) {
        const data = await res.json();
        if (signal?.aborted) return;
        const overall = data?.overallStats || {};
        const avgProb = ((overall.avgWonProbability || 0) + (overall.avgLostProbability || 0)) / 2;
        setStats({
          totalOpportunities: overall.totalOpportunities || 0,
          wonOpportunities: overall.wonOpportunities || 0,
          totalValue: overall.totalValue || 0,
          averageProbability: isNaN(avgProb) ? 0 : avgProb
        });
      }
    } catch (err) {
      if (err?.name === 'AbortError') return;
      console.error('Failed to fetch stats:', err);
    } finally {
      if (!signal?.aborted) setStatsLoading(false);
    }
  };

  useEffect(() => {
    const controller = new AbortController();
    abortControllerRef.current = controller;
    deferredSelectionTimeoutRef.current = setTimeout(() => {
      loadSelectionValues();
    }, 150);
    deferredStatsTimeoutRef.current = setTimeout(() => {
      fetchStats();
    }, 200);
    return () => {
      if (deferredSelectionTimeoutRef.current) {
        clearTimeout(deferredSelectionTimeoutRef.current);
        deferredSelectionTimeoutRef.current = null;
      }
      if (deferredStatsTimeoutRef.current) {
        clearTimeout(deferredStatsTimeoutRef.current);
        deferredStatsTimeoutRef.current = null;
      }
      controller.abort();
      abortControllerRef.current = null;
    };
  }, []);

  const filtersKey = useMemo(() => JSON.stringify(advancedFilters), [advancedFilters]);
  const listDepsRef = useRef({ filtersKey, salespersonFilter, selectedTag });

  // Single effect: fetch opportunities when page or any list filter changes (avoids duplicate/canceled requests)
  useEffect(() => {
    const filterRelatedChanged =
      listDepsRef.current.filtersKey !== filtersKey ||
      listDepsRef.current.salespersonFilter !== salespersonFilter ||
      listDepsRef.current.selectedTag !== selectedTag;
    listDepsRef.current = { filtersKey, salespersonFilter, selectedTag };
    fetchOpportunities(filterRelatedChanged);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.page, filtersKey, salespersonFilter, selectedTag]);

  useEffect(() => {
    if (hasActiveFilters) setShowFilters(true);
  }, [hasActiveFilters]);

  useEffect(() => {
    if (!hasRole(['Admin', 'Manager'])) return;
    let cancelled = false;
    const run = async () => {
      try {
        const selectedRole = localStorage.getItem('selectedRole');
        const response = await fetch('/api/users/salespersons', {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            ...(selectedRole ? { 'X-Selected-Role': selectedRole } : {})
          }
        });
        if (cancelled || !response.ok) return;
        const data = await response.json();
        const salespersonOnly = (data.salespersons || []).filter(u => (u.Roles || '').includes('Salesperson'));
        if (!cancelled) setSalespersons(salespersonOnly);
      } catch (error) {
        if (!cancelled) console.error('Error fetching salespersons:', error);
      }
    };
    deferredSalespersonsTimeoutRef.current = setTimeout(() => {
      run();
    }, 250);
    return () => {
      cancelled = true;
      if (deferredSalespersonsTimeoutRef.current) {
        clearTimeout(deferredSalespersonsTimeoutRef.current);
        deferredSalespersonsTimeoutRef.current = null;
      }
    };
  }, [hasRole]);

  // Close salesperson filter dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showSalespersonFilter && !event.target.closest('.salesperson-filter-container')) {
        setShowSalespersonFilter(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showSalespersonFilter]);

  // Handle opportunityId query parameter for scrolling to specific opportunity
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const opportunityId = urlParams.get('opportunityId');
    
    if (opportunityId && !hasScrolledToOpportunity && opportunities.length > 0) {
      setFocusedOpportunityId(parseInt(opportunityId));
      
      // Wait a bit for the DOM to render, then scroll to the opportunity
      setTimeout(() => {
        const opportunityRef = opportunityRefs.current[opportunityId];
        if (opportunityRef) {
          opportunityRef.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'center' 
          });
          setHasScrolledToOpportunity(true);
          
          // Add a temporary highlight effect
          opportunityRef.style.backgroundColor = '#fef3c7';
          opportunityRef.style.transition = 'background-color 0.3s ease';
          setTimeout(() => {
            opportunityRef.style.backgroundColor = '';
          }, 2000);
        }
      }, 500);
    }
  }, [opportunities, location.search, hasScrolledToOpportunity]);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchOpportunities(true);
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const clearFilters = () => {
    setFilters({
      search: '',
      stage: '',
      priority: '',
      category: '',
      industry: '',
      minValue: '',
      maxValue: '',
      probability: ''
    });
    setSelectedTag('');
    setTimeout(() => fetchOpportunities(true), 100);
  };

  const handleCreateOpportunity = () => {
    setSelectedOpportunity(null);
    setFormMode('create');
    setShowForm(true);
  };

  const handleEditOpportunity = async (opportunity) => {
    try {
      setLoading(true);
      const selectedRole = localStorage.getItem('selectedRole');
      const res = await fetch(`/api/opportunities/${opportunity.OpportunityID}`, {
        headers: { 
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          ...(selectedRole ? { 'X-Selected-Role': selectedRole } : {})
        }
      });
      if (!res.ok) throw new Error('Failed to load opportunity');
      const data = await res.json();
      setSelectedOpportunity(data.opportunity);
      setFormMode('edit');
      setShowForm(true);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleViewOpportunity = async (opportunity) => {
    try {
      setLoading(true);
      const selectedRole = localStorage.getItem('selectedRole');
      const res = await fetch(`/api/opportunities/${opportunity.OpportunityID}`, {
        headers: { 
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          ...(selectedRole ? { 'X-Selected-Role': selectedRole } : {})
        }
      });
      if (!res.ok) throw new Error('Failed to load opportunity');
      const data = await res.json();
      setSelectedOpportunity(data.opportunity || data);
      setShowDetail(true);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const [deleteDialog, setDeleteDialog] = useState({
    isOpen: false,
    opportunityId: null,
    dependencies: null
  });

  const handleDeleteOpportunity = async (opportunityId, confirmDeleteDependencies = false) => {
    // If not confirming dependencies, show initial confirmation
    if (!confirmDeleteDependencies) {
      if (!window.confirm(safeT('common.confirmDelete', 'Are you sure you want to delete this item?'))) return;
    }
    
    try {
      const selectedRole = localStorage.getItem('selectedRole');
      const url = `/api/opportunities/${opportunityId}${confirmDeleteDependencies ? '?confirmDeleteDependencies=true' : ''}`;
      const res = await fetch(url, { 
        method: 'DELETE', 
        headers: { 
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          ...(selectedRole ? { 'X-Selected-Role': selectedRole } : {})
        }
      });
      
      const responseData = await res.json().catch(() => ({}));
      
      if (!res.ok) {
        // Check if this is a dependency confirmation required response
        if (res.status === 409 && responseData.requiresConfirmation) {
          setDeleteDialog({
            isOpen: true,
            opportunityId: opportunityId,
            dependencies: responseData.dependencies
          });
          return;
        }
        throw new Error(responseData.message || 'Failed to delete opportunity');
      }
      
      await Promise.all([fetchOpportunities(), fetchStats()]);
      setError(null);
      setDeleteDialog({ isOpen: false, opportunityId: null, dependencies: null });
      
      if (responseData.deletedDependencies) {
        toast.success(`Opportunity deleted successfully. Also deleted: ${responseData.deletedDependencies.join(', ')}`);
      } else {
        toast.success('Opportunity deleted successfully');
      }
    } catch (err) {
      setError(err.message);
      setDeleteDialog({ isOpen: false, opportunityId: null, dependencies: null });
    }
  };

  const handleConfirmDeleteWithDependencies = () => {
    if (deleteDialog.isBulk) {
      handleConfirmBulkDeleteWithDependencies();
    } else if (deleteDialog.opportunityId) {
      handleDeleteOpportunity(deleteDialog.opportunityId, true);
    }
  };

  const handleFormSubmit = async (formData) => {
    try {
      console.log('Form data being submitted:', formData);
      const url = formMode === 'create' ? '/api/opportunities' : `/api/opportunities/${selectedOpportunity.OpportunityID}`;
      const method = formMode === 'create' ? 'POST' : 'PUT';
      const res = await fetch(url, { 
        method, 
        headers: { 
          'Content-Type': 'application/json', 
          'Authorization': `Bearer ${localStorage.getItem('token')}` 
        }, 
        body: JSON.stringify(formData) 
      });
      
      console.log('Response status:', res.status);
      
      if (!res.ok) {
        const errorData = await res.json();
        console.log('Error response:', errorData);
        throw new Error(errorData.message || 'Failed to save opportunity');
      }
      
      const responseData = await res.json();
      console.log('Success response:', responseData);
      
      setShowForm(false);
      await Promise.all([fetchOpportunities(), fetchStats()]);
      setError(null);
    } catch (err) {
      console.error('Form submission error:', err);
      setError(err.message);
      // Re-throw the error so the form can handle it
      throw err;
    }
  };

  // Spreadsheet handlers
  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedOpportunities(new Set());
      setSelectAll(false);
    } else {
      const allIds = opportunities.map(opp => opp.OpportunityID);
      setSelectedOpportunities(new Set(allIds));
      setSelectAll(true);
    }
  };

  // Handle bulk delete
  const handleBulkDelete = async (confirmDeleteDependencies = false) => {
    if (selectedOpportunities.size === 0) {
      toast.error('Please select at least one opportunity to delete');
      return;
    }

    // If not confirming dependencies, show initial confirmation
    if (!confirmDeleteDependencies) {
      const count = selectedOpportunities.size;
      if (!window.confirm(
        `Are you sure you want to delete ${count} opportunity${count !== 1 ? '\'s' : ''}? This action cannot be undone.`
      )) {
        return;
      }
    }

    try {
      setLoading(true);
      setError(null);
      
      const opportunityIds = Array.from(selectedOpportunities);
      const selectedRole = localStorage.getItem('selectedRole');
      const response = await fetch('/api/opportunities/bulk-delete', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          ...(selectedRole ? { 'X-Selected-Role': selectedRole } : {})
        },
        body: JSON.stringify({ 
          opportunityIds,
          confirmDeleteDependencies 
        })
      });

      const responseData = await response.json().catch(() => ({}));

      if (!response.ok) {
        // Check if this is a dependency confirmation required response
        if (response.status === 409 && responseData.requiresConfirmation) {
          setDeleteDialog({
            isOpen: true,
            opportunityId: null, // Use null to indicate bulk delete
            dependencies: responseData.dependencies,
            isBulk: true,
            entityIds: opportunityIds
          });
          setLoading(false);
          return;
        }
        throw new Error(responseData.message || 'Failed to delete opportunities');
      }

      const result = responseData;
      
      toast.success(
        `Successfully deleted ${result.deletedCount || opportunityIds.length} opportunity${result.deletedCount !== 1 ? '\'s' : ''}`
      );
      
      // Clear selection
      setSelectedOpportunities(new Set());
      setSelectAll(false);
      
      // Refresh the list
      await Promise.all([fetchOpportunities(), fetchStats()]);
      setDeleteDialog({ isOpen: false, opportunityId: null, dependencies: null });
    } catch (err) {
      setError(err.message);
      setDeleteDialog({ isOpen: false, opportunityId: null, dependencies: null });
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmBulkDeleteWithDependencies = () => {
    if (deleteDialog.isBulk && deleteDialog.entityIds) {
      handleBulkDelete(true);
    }
  };

  // Handle bulk tag manager success
  const handleBulkTagSuccess = () => {
    // Refresh the opportunity list to show updated tags
    fetchOpportunities();
  };

  const handleCellSave = async (opportunityId, fieldName, newValue) => {
    try {
      // Build update object - for custom attributes, ensure proper structure
      const updateData = {};
      
      if (fieldName.startsWith('attr_')) {
        // Custom attribute
        const attributeName = fieldName.substring(5); // Remove 'attr_' prefix
        updateData.attributes = { [attributeName]: newValue };
      } else {
        // Regular field
        updateData[fieldName] = newValue;
      }
      
      const selectedRole = localStorage.getItem('selectedRole');
      const res = await fetch(`/api/opportunities/${opportunityId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          ...(selectedRole ? { 'X-Selected-Role': selectedRole } : {})
        },
        body: JSON.stringify(updateData)
      });
      
      if (!res.ok) {
        throw new Error('Failed to update opportunity');
      }
      
      // Refresh opportunities to show updated data
      await fetchOpportunities();
      
      return true;
    } catch (error) {
      console.error('Error saving opportunity cell:', error);
      throw error;
    }
  };

  const getStageColor = (stage) => {
    // Find the stage option to get its color
    const stageOption = stageOptions.find(opt => opt.value === stage);
    if (stageOption && stageOption.color) {
      const colorMap = {
        'red': 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200',
        'orange': 'bg-orange-100 dark:bg-orange-900 text-orange-800 dark:text-orange-200',
        'yellow': 'bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200',
        'green': 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200',
        'gray': 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200',
        'blue': 'bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200',
        'purple': 'bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200'
      };
      return colorMap[stageOption.color] || 'bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200';
    }
    
    // Fallback to original logic for backward compatibility
    switch (stage) {
      case 'Qualified': return 'bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200';
      case 'Proposal': return 'bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200';
      case 'Negotiation': return 'bg-orange-100 dark:bg-orange-900 text-orange-800 dark:text-orange-200';
      case 'Closed Won': return 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200';
      case 'Closed Lost': return 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200';
      default: return 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200';
    }
  };

  const getPriorityColor = (priority) => {
    // Find the priority option to get its color
    const priorityOption = priorityOptions.find(opt => opt.value === priority);
    if (priorityOption && priorityOption.color) {
      const colorMap = {
        'red': 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200',
        'orange': 'bg-orange-100 dark:bg-orange-900 text-orange-800 dark:text-orange-200',
        'yellow': 'bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200',
        'green': 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200',
        'gray': 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200',
        'blue': 'bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200',
        'purple': 'bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200'
      };
      return colorMap[priorityOption.color] || 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200';
    }
    
    // Fallback to original logic for backward compatibility
    switch (priority) {
      case 'Critical': return 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200';
      case 'High': return 'bg-orange-100 dark:bg-orange-900 text-orange-800 dark:text-orange-200';
      case 'Medium': return 'bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200';
      case 'Low': return 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200';
      default: return 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200';
    }
  };

  const getProbabilityColor = (probability) => {
    if (probability >= 80) return 'text-green-600 dark:text-green-400';
    if (probability >= 60) return 'text-yellow-600 dark:text-yellow-400';
    if (probability >= 40) return 'text-orange-600 dark:text-orange-400';
    return 'text-red-600 dark:text-red-400';
  };

  // Helper function to format currency (now using utility)
  const formatCurrencyLocal = (amount, currency = 'THB') => {
    return formatCurrency(amount, currency);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString();
  };

  // Custom render functions for DynamicEntityTable
  const customRender = useMemo(() => ({
    OpportunityName: (opportunity) => (
      <div className="flex items-center">
        <div className="flex-1">
          <div className="text-sm font-medium text-gray-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors duration-150">
            {opportunity.OpportunityName || `Opportunity #${opportunity.OpportunityID}`}
          </div>
          {opportunity.Description && (
            <div className="text-sm text-gray-500 dark:text-gray-400 truncate max-w-xs">
              {opportunity.Description.substring(0, 50)}...
            </div>
          )}
        </div>
      </div>
    ),
    SalespersonID: (opportunity) => (
      <div className="flex items-center space-x-2">
        <div className="flex-shrink-0 h-8 w-8 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center">
          <User className="h-4 w-4 text-blue-600 dark:text-blue-400" />
        </div>
        <span className="text-sm text-gray-900 dark:text-white">
          {opportunity.SalespersonName || 'N/A'}
        </span>
      </div>
    ),
    CustomerName: (opportunity) => (
      <div>
        <div className="text-sm text-gray-900 dark:text-white">
          {opportunity.CustomerName || '-'}
        </div>
        <div className="text-sm text-gray-500 dark:text-gray-400">
          {opportunity.ContactName || '-'}
        </div>
      </div>
    ),
    Value: (opportunity) => (
      <div className="text-sm font-medium text-gray-900 dark:text-white">
        {formatCurrencyLocal(opportunity.Value, opportunity.Currency)}
      </div>
    ),
    Stage: (opportunity) => (
      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStageColor(opportunity.Stage)}`}>
        {opportunity.Stage}
      </span>
    ),
    Probability: (opportunity) => (
      <div className={`text-sm font-medium ${getProbabilityColor(opportunity.Probability)}`}>
        {opportunity.Probability}%
      </div>
    ),
    Priority: (opportunity) => (
      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getPriorityColor(opportunity.Priority)}`}>
        {opportunity.Priority}
      </span>
    ),
    ExpectedCloseDate: (opportunity) => (
      <span className="text-sm text-gray-500 dark:text-gray-400">
        {formatDate(opportunity.ExpectedCloseDate)}
      </span>
    )
  }), [priorityOptions, stageOptions]);

  // Spreadsheet view functions
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Build spreadsheet columns
  const spreadsheetColumns = useMemo(() => {
    if (!entityConfig || !entityConfig.fields) return [];
    
    // Get fields that should show in list
    const fields = Object.keys(entityConfig.fields)
      .map(fieldName => {
        const field = entityConfig.fields[fieldName];
        if (!field?.ui?.showInList) return null;
        return {
          name: fieldName,
          ...field,
          order: field.ui.order || 999
        };
      })
      .filter(Boolean)
      .sort((a, b) => (a.order ?? 999) - (b.order ?? 999));
    
    return fields.map(field => ({
      key: field.name,
      label: field.displayName || field.name,
      width: '150px',
      editable: field.database?.table === 'Opportunities', // Only direct table columns editable
      fieldName: field.name,
      fieldConfig: field
    }));
  }, [entityConfig]);

  const getSpreadsheetCellValue = (opportunity, columnKey, fieldConfig) => {
    const rawValue = opportunity[columnKey];
    
    // Handle SalespersonID - show name instead of ID
    if (columnKey === 'SalespersonID') {
      return opportunity.SalespersonName || '-';
    }
    
    if (rawValue === null || rawValue === undefined || rawValue === '') {
      return '-';
    }
    
    // Check if this is a select field and get label
    if (fieldConfig && checkIsSelectField(entityConfig, columnKey)) {
      const fieldOptions = getFieldOptionsSync(entityConfig, columnKey);
      const option = fieldOptions.find(opt => opt.value === rawValue);
      return option ? (option.label || option.value) : rawValue;
    }
    
    // Format dates
    if (fieldConfig?.dataType === 'date' && rawValue) {
      try {
        return new Date(rawValue).toLocaleDateString();
      } catch {
        return rawValue;
      }
    }
    
    if (fieldConfig?.dataType === 'datetime' && rawValue) {
      try {
        return new Date(rawValue).toLocaleString();
      } catch {
        return rawValue;
      }
    }
    
    // Format currency
    if (columnKey === 'Value' && rawValue) {
      return formatCurrencyLocal(rawValue, opportunity.Currency);
    }
    
    return String(rawValue);
  };

  const isFieldEditable = (opportunity, columnKey, fieldConfig) => {
    if (!fieldConfig) return null;
    
    const dbTable = fieldConfig.database?.table;
    
    // Only fields from Opportunities table are editable
    if (dbTable === 'Opportunities') {
      return columnKey;
    }
    
    // Custom attributes are always editable
    if (fieldConfig.storage?.type === 'attributes') {
      return columnKey;
    }
    
    return null;
  };

  if (showForm) {
    return (
      <OpportunityForm 
        opportunity={selectedOpportunity} 
        mode={formMode} 
        onSubmit={handleFormSubmit} 
        onCancel={() => setShowForm(false)} 
      />
    );
  }

  if (showDetail) {
    return (
      <OpportunityDetail 
        opportunity={selectedOpportunity} 
        onEdit={() => {
          setShowDetail(false);
          setFormMode('edit');
          setShowForm(true);
        }}
        onClose={() => setShowDetail(false)} 
      />
    );
  }

  const showTranslationLoading = !translationsReady || !ready;

  return (
    <div className="space-y-3 sm:space-y-4 md:space-y-6">
      {showTranslationLoading && (
        <div className="text-xs text-gray-500 dark:text-gray-400">
          Loading translations...
        </div>
      )}
      {/* Header - Title and Actions in Same Line */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
        {/* Title Section */}
        <div className="flex-shrink-0">
          <div className="flex items-center space-x-3">
            <img 
              src="icon/lead.png" 
              alt="Opportunities Icon" 
              className="w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 object-contain"
            />
            <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 dark:text-white">{safeT('nav.opportunities', 'Opportunities')}</h1>
          </div>
          <p className="text-xs sm:text-sm md:text-base text-gray-600 dark:text-gray-300 mt-1">{safeT('opportunities.subtitle', 'Manage and track your sales opportunities')}</p>
          <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 mt-1 hidden sm:block">{safeT('opportunities.clickToView', 'Click on any row to view details')}</p>
        </div>
        
        {/* Action Buttons - Collapsible Menu */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-2">
          {/* View Mode Toggle - Always Visible */}
          <div className="flex items-center bg-gray-100 dark:bg-gray-700 rounded-lg p-1 w-full sm:w-auto">
            <button
              onClick={() => setViewMode('list')}
              className={`flex-1 sm:flex-none px-2 sm:px-3 py-1.5 rounded-md flex items-center justify-center space-x-1 sm:space-x-2 transition-colors touch-target ${
                viewMode === 'list'
                  ? 'bg-white dark:bg-gray-600 text-blue-600 dark:text-blue-400 shadow-sm'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
              }`}
              title="List View"
            >
              <List className="h-4 w-4" />
              <span className="text-xs sm:text-sm font-medium">List</span>
            </button>
            <button
              onClick={() => setViewMode('spreadsheet')}
              className={`flex-1 sm:flex-none px-2 sm:px-3 py-1.5 rounded-md flex items-center justify-center space-x-1 sm:space-x-2 transition-colors touch-target ${
                viewMode === 'spreadsheet'
                  ? 'bg-white dark:bg-gray-600 text-blue-600 dark:text-blue-400 shadow-sm'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
              }`}
              title="Spreadsheet View"
            >
              <Grid className="h-4 w-4" />
              <span className="text-xs sm:text-sm font-medium">Spreadsheet</span>
            </button>
          </div>
          
          {/* Filter Button - Between View Toggle and Action Buttons */}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`px-3 py-2 rounded-lg flex items-center justify-center space-x-2 text-xs sm:text-sm font-medium touch-target no-tap-highlight transition-all ${
              hasActiveFilters
                ? 'bg-orange-500 dark:bg-orange-600 text-white hover:bg-orange-600 dark:hover:bg-orange-700'
                : showFilters
                ? 'bg-gray-600 dark:bg-gray-500 text-white hover:bg-gray-700 dark:hover:bg-gray-600'
                : 'bg-gray-500 dark:bg-gray-600 text-white hover:bg-gray-600 dark:hover:bg-gray-700'
            }`}
            title="Toggle filters"
          >
            <Filter className="h-4 w-4 flex-shrink-0" />
            <span className="hidden sm:inline">{safeT('common.filter', 'Filter')}</span>
            {hasActiveFilters && (
              <span className="bg-white/20 px-1.5 py-0.5 rounded-full text-xs font-bold">
                {activeFiltersCount}
              </span>
            )}
          </button>
          
          {/* Salesperson Filter (Admin/Manager only) */}
          {hasRole(['Admin', 'Manager']) && salespersons.length > 0 && (
            <div className="relative salesperson-filter-container">
              <button
                onClick={() => setShowSalespersonFilter(!showSalespersonFilter)}
                className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg border transition-colors text-xs sm:text-sm font-medium ${
                  salespersonFilter !== 'all'
                    ? 'bg-purple-50 border-purple-200 text-purple-700 dark:bg-purple-900/20 dark:border-purple-700 dark:text-purple-300'
                    : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700'
                }`}
                title={safeT('opportunities.filterBySalesperson', 'Filter by salesperson')}
              >
                <User className="h-4 w-4" />
                <span className="hidden sm:inline truncate max-w-32">
                  {salespersonFilter === 'all' 
                    ? safeT('opportunities.allSalespersons', 'All Salespersons')
                    : (() => {
                        const selected = salespersons.find(s => String(s.UserID) === String(salespersonFilter));
                        return selected ? `${selected.FirstName} ${selected.LastName}` : safeT('opportunities.allSalespersons', 'All Salespersons');
                      })()
                  }
                </span>
                <ChevronDown className="h-4 w-4" />
              </button>
              
              {showSalespersonFilter && (
                <div className="absolute top-full right-0 mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg shadow-lg z-50 min-w-56 max-h-72 overflow-y-auto">
                  <div className="p-2 space-y-1">
                    <button
                      onClick={() => {
                        setSalespersonFilter('all');
                        setShowSalespersonFilter(false);
                      }}
                      className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors ${
                        salespersonFilter === 'all'
                          ? 'bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300'
                          : 'text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700'
                      }`}
                    >
                      {safeT('opportunities.allSalespersons', 'All Salespersons')}
                    </button>
                    {salespersons.map(salesperson => (
                      <button
                        key={salesperson.UserID}
                        onClick={() => {
                          setSalespersonFilter(String(salesperson.UserID));
                          setShowSalespersonFilter(false);
                        }}
                        className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors flex items-center gap-2 ${
                          String(salespersonFilter) === String(salesperson.UserID)
                            ? 'bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300'
                            : 'text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700'
                        }`}
                      >
                        <div className="w-6 h-6 rounded-full bg-gray-200 dark:bg-gray-600 flex items-center justify-center text-xs font-medium">
                          {salesperson.FirstName?.charAt(0)}{salesperson.LastName?.charAt(0)}
                        </div>
                        <span className="truncate">{salesperson.FirstName} {salesperson.LastName}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          
          {/* Action Buttons Row */}
          <div className="flex flex-wrap items-center justify-end gap-2">
            {/* Expandable Menu Buttons - Always visible on mobile, collapsible on desktop */}
            <div className={`flex flex-wrap items-center gap-2 ${menuExpanded ? 'sm:flex' : 'sm:hidden'}`}>
              <button 
                onClick={() => Promise.all([fetchOpportunities(true), fetchStats()])}
                disabled={loading || statsLoading}
                className="bg-gray-600 dark:bg-gray-500 text-white px-3 py-2 rounded-lg flex items-center justify-center space-x-1 sm:space-x-2 text-xs sm:text-sm hover:bg-gray-700 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed touch-target no-tap-highlight"
                title={safeT('common.refresh', 'Refresh')}
              >
                <RefreshCw className={`h-4 w-4 flex-shrink-0 ${(loading || statsLoading) ? 'animate-spin' : ''}`} />
                <span className="hidden sm:inline">{safeT('common.refresh', 'Refresh')}</span>
                <span className="sm:hidden">Refresh</span>
              </button>
            </div>
            
            {/* Expand/Collapse Button - Only on Desktop */}
            <button
              onClick={() => setMenuExpanded(!menuExpanded)}
              className="hidden sm:flex bg-gray-500 dark:bg-gray-600 text-white px-3 py-2 rounded-lg hover:bg-gray-600 dark:hover:bg-gray-700 items-center justify-center space-x-1 sm:space-x-2 text-xs sm:text-sm touch-target no-tap-highlight transition-transform"
              title={menuExpanded ? 'Collapse menu' : 'Expand menu'}
            >
              <MoreVertical className="h-4 w-4 flex-shrink-0" />
              <ChevronDown className={`h-3 w-3 flex-shrink-0 transition-transform duration-200 ${menuExpanded ? 'rotate-180' : ''}`} />
            </button>
            
            {/* Create Opportunity Button - Always Visible */}
            <button 
              onClick={handleCreateOpportunity} 
              className="bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-600 hover:to-cyan-600 dark:from-teal-600 dark:to-cyan-600 dark:hover:from-teal-700 dark:hover:to-cyan-700 text-white px-4 sm:px-5 py-2.5 sm:py-3 rounded-lg flex items-center justify-center space-x-2 text-sm sm:text-base font-semibold touch-target no-tap-highlight shadow-md transition-all duration-200"
            >
              <Plus className="h-5 w-5 flex-shrink-0" />
              <span>{safeT('opportunities.createOpportunity', 'Create Opportunity')}</span>
            </button>
          </div>
        </div>
      </div>

      {/* Stats Cards - Mobile Optimized */}
      <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-4 gap-2 sm:gap-3 md:gap-4">
        <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-300">{safeT('opportunities.totalOpportunities', 'Total Opportunities')}</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {statsLoading ? (
                  <RefreshCw className="h-6 w-6 animate-spin text-gray-400 dark:text-gray-500" />
                ) : (
                  stats.totalOpportunities || 0
                )}
              </p>
            </div>
            <Trophy className="h-8 w-8 text-blue-500 dark:text-blue-400" />
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-300">{safeT('opportunities.totalValue', 'Total Value')}</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {statsLoading ? (
                  <RefreshCw className="h-6 w-6 animate-spin text-gray-400 dark:text-gray-500" />
                ) : (
                  formatCurrencyLocal(stats.totalValue)
                )}
              </p>
            </div>
            <DollarSign className="h-8 w-8 text-green-500 dark:text-green-400" />
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-300">{safeT('opportunities.averageProbability', 'Average Probability')}</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {statsLoading ? (
                  <RefreshCw className="h-6 w-6 animate-spin text-gray-400 dark:text-gray-500" />
                ) : (
                  `${stats.averageProbability ? Math.round(stats.averageProbability) : 0}%`
                )}
              </p>
            </div>
            <Star className="h-8 w-8 text-yellow-500 dark:text-yellow-400" />
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-300">{safeT('opportunities.wonOpportunities', 'Won Opportunities')}</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {statsLoading ? (
                  <RefreshCw className="h-6 w-6 animate-spin text-gray-400 dark:text-gray-500" />
                ) : (
                  stats.wonOpportunities || 0
                )}
              </p>
            </div>
            <Award className="h-8 w-8 text-green-500 dark:text-green-400" />
          </div>
        </div>
      </div>

      {/* Bulk Action Bar - Shows when opportunities are selected */}
      {selectedOpportunities.size > 0 && (
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-4">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center space-x-3">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                {selectedOpportunities.size} {selectedOpportunities.size === 1 ? 'opportunity' : 'opportunities'} selected
              </span>
            </div>
            
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setShowBulkTagManager(true)}
                disabled={loading}
                className="bg-blue-600 dark:bg-blue-500 text-white px-3 py-2 rounded-lg hover:bg-blue-700 dark:hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2 text-sm font-medium"
                title="Manage tags for selected opportunities"
              >
                <Tag className="h-4 w-4" />
                <span>Tag</span>
              </button>
              
              <button
                onClick={handleBulkDelete}
                disabled={loading}
                className="bg-red-600 dark:bg-red-500 text-white px-3 py-2 rounded-lg hover:bg-red-700 dark:hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2 text-sm font-medium"
                title="Delete selected opportunities"
              >
                <Trash2 className="h-4 w-4" />
                <span>Delete</span>
              </button>
              
              <button
                onClick={() => {
                  setSelectedOpportunities(new Set());
                  setSelectAll(false);
                }}
                className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
                title="Clear selection"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Filters - Hidden by Default */}
      {showFilters && (
        <div className="space-y-4">
          {/* Legacy Simple Filters - Keep for backward compatibility */}
          <div className={`p-3 sm:p-4 rounded-lg shadow border transition-colors duration-200 ${
            hasSimpleFilters
              ? 'bg-orange-50 dark:bg-orange-900/20 border-orange-300 dark:border-orange-700'
              : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700'
          }`}>
            {hasSimpleFilters && (
              <div className="mb-2 flex items-center gap-2">
                <span className="text-xs font-medium text-orange-700 dark:text-orange-300">
                  🔍 Simple Filters Active
                </span>
                <button
                  type="button"
                  onClick={clearFilters}
                  className="text-xs text-orange-600 dark:text-orange-400 hover:underline"
                >
                  Clear All
                </button>
              </div>
            )}
            <form onSubmit={handleSearch} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400 dark:text-gray-500" />
              <input
                type="text"
                placeholder={safeT('opportunities.searchPlaceholder', 'Search opportunities...')}
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                className="w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
              />
            </div>
            
            <select
              value={filters.stage}
              onChange={(e) => handleFilterChange('stage', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              <option value="">{safeT('opportunities.allStages', 'All Stages')}</option>
              {stageOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>

            <select
              value={filters.priority}
              onChange={(e) => handleFilterChange('priority', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              <option value="">{safeT('opportunities.allPriorities', 'All Priorities')}</option>
              {priorityOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>

            <select
              value={filters.category}
              onChange={(e) => handleFilterChange('category', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              <option value="">{safeT('opportunities.allCategories', 'All Categories')}</option>
              {categoryOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>

            <select
              value={selectedTag}
              onChange={(e) => setSelectedTag(e.target.value)}
              onFocus={handleTagDropdownFocus}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              title="Filter by tag"
            >
              <option value="">{safeT('opportunities.allTags', 'All Tags')}</option>
              {availableTags && availableTags.length > 0 ? (
                availableTags.map((tag) => (
                  <option key={tag.id || tag.name} value={tag.name}>
                    {tag.name} ({tag.usageCount || 0})
                  </option>
                ))
              ) : tagsLoading ? (
                <option value="" disabled>Loading tags...</option>
              ) : null}
            </select>
          </div>

          <div className="flex justify-end items-center">
            <button
              type="submit"
              className="bg-gray-600 dark:bg-gray-500 text-white px-3 sm:px-4 py-2 rounded-lg hover:bg-gray-700 dark:hover:bg-gray-600 flex items-center justify-center space-x-1 sm:space-x-2 text-sm sm:text-base touch-target no-tap-highlight"
            >
              <Filter className="h-4 w-4" />
              <span>{safeT('common.filter', 'Filter')}</span>
            </button>
          </div>
        </form>
        </div>
        
          {/* EntityFilter Component */}
          <EntityFilter
            entityName="opportunity"
            filters={advancedFilters}
            onFiltersChange={(filters) => {
              console.log('🔍 Opportunities: Filters changed in EntityFilter:', filters);
              setAdvancedFilters(filters);
              setPagination(prev => ({ ...prev, page: 1 })); // Reset to first page when filters change
            }}
          />
        </div>
      )}

      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <div className="flex items-center space-x-2">
            <X className="h-4 w-4 text-red-500 dark:text-red-400" />
            <p className="text-red-700 dark:text-red-300">{error}</p>
          </div>
        </div>
      )}

      {/* Opportunities Table - List or Spreadsheet View */}
      {loading ? (
        <div className="flex justify-center py-8">
          <RefreshCw className="h-6 w-6 animate-spin text-gray-400 dark:text-gray-500" />
        </div>
      ) : (
        <div>
          {viewMode === 'list' ? (
            <DynamicEntityTable
              entityName="opportunity"
              data={opportunities}
              loading={loading}
              onView={handleViewOpportunity}
              onEdit={handleEditOpportunity}
              onDelete={handleDeleteOpportunity}
              customRender={customRender}
              onRowDoubleClick={handleViewOpportunity}
              refs={opportunityRefs}
              onSelect={(id) => {
                setSelectedOpportunities(prev => {
                  const newSet = new Set(prev);
                  if (newSet.has(id)) {
                    newSet.delete(id);
                  } else {
                    newSet.add(id);
                  }
                  return newSet;
                });
              }}
              selectedItems={selectedOpportunities}
              selectAll={selectAll}
              onSelectAll={handleSelectAll}
            />
          ) : (
            <SpreadsheetView
              entityName="opportunity"
              entityConfig={entityConfig}
              data={opportunities}
              columns={spreadsheetColumns}
              loading={loading}
              onView={handleViewOpportunity}
              onEdit={handleEditOpportunity}
              onDelete={handleDeleteOpportunity}
              onCellSave={handleCellSave}
              onSelect={(id) => {
                setSelectedOpportunities(prev => {
                  const newSet = new Set(prev);
                  if (newSet.has(id)) {
                    newSet.delete(id);
                  } else {
                    newSet.add(id);
                  }
                  return newSet;
                });
              }}
              selectedItems={selectedOpportunities}
              selectAll={selectAll}
              onSelectAll={handleSelectAll}
              refs={opportunityRefs}
              getDisplayValue={getSpreadsheetCellValue}
              isFieldEditable={isFieldEditable}
              sortConfig={sortConfig}
              onSort={handleSort}
              i18n={i18n}
            />
          )}
          
          {/* Pagination */}
          {pagination.pages > 1 && (
            <div className="bg-white dark:bg-gray-800 rounded-b-lg shadow border-t border-gray-200 dark:border-gray-700 px-4 py-3 flex items-center justify-between mt-4">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                disabled={pagination.page === 1}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50"
              >
                {safeT('common.previous', 'Previous')}
              </button>
              <button
                onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                disabled={pagination.page === pagination.pages}
                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50"
              >
                {safeT('common.next', 'Next')}
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700 dark:text-gray-300">
                  <span className="text-xs sm:text-sm">{safeT('common.showing', 'Showing')}</span> <span className="text-xs sm:text-sm font-medium">{((pagination.page - 1) * pagination.limit) + 1}</span> <span className="text-xs sm:text-sm">{safeT('common.to', 'to')}</span> <span className="text-xs sm:text-sm font-medium">{Math.min(pagination.page * pagination.limit, pagination.total)}</span> <span className="text-xs sm:text-sm">{safeT('common.of', 'of')}</span> <span className="text-xs sm:text-sm font-medium">{pagination.total}</span> <span className="text-xs sm:text-sm">{safeT('common.results', 'results')}</span>
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                  <button
                    onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                    disabled={pagination.page === 1}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm font-medium text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50"
                  >
                    {safeT('common.previous', 'Previous')}
                  </button>
                  {Array.from({ length: pagination.pages }, (_, i) => i + 1).map(pageNum => (
                    <button
                      key={pageNum}
                      onClick={() => setPagination(prev => ({ ...prev, page: pageNum }))}
                      className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                        pageNum === pagination.page
                          ? 'z-10 bg-blue-50 dark:bg-blue-900/30 border-blue-500 dark:border-blue-400 text-blue-600 dark:text-blue-400'
                          : 'bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-600'
                      }`}
                    >
                      {pageNum}
                    </button>
                  ))}
                  <button
                    onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                    disabled={pagination.page === pagination.pages}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm font-medium text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50"
                  >
                    {safeT('common.next', 'Next')}
                  </button>
                </nav>
              </div>
            </div>
            </div>
          )}
        </div>
      )}

      {/* Bulk Tag Manager Modal */}
      <BulkTagManager
        isOpen={showBulkTagManager}
        onClose={() => setShowBulkTagManager(false)}
        entityType="Opportunity"
        entityIds={Array.from(selectedOpportunities)}
        entityCount={selectedOpportunities.size}
        onSuccess={handleBulkTagSuccess}
      />
      
      {/* Dependency Delete Dialog */}
      <DependencyDeleteDialog
        isOpen={deleteDialog.isOpen}
        onClose={() => setDeleteDialog({ isOpen: false, opportunityId: null, dependencies: null })}
        onConfirm={handleConfirmDeleteWithDependencies}
        entityName="opportunity"
        dependencies={deleteDialog.dependencies}
      />
    </div>
  );
};

export default Opportunities; 